/**
 * The one shared world-to-screen projection (AD-10). Focus-centred SI world
 * coordinates (metres, origin at the current central body's focus, +y up) map to
 * screen pixels, folding in zoom, pan, device-pixel-ratio, and axis orientation.
 *
 * This is defined once and consumed identically by the Canvas2D adapter (and, in
 * later stories, the WebGL adapter and the pointer hit-test / drag inverse). No
 * adapter or component computes its own transform. "Fit all orbits" derives from
 * this projection; collision decisions use core SI radii, never projected pixels.
 */

export interface Viewport {
  /** CSS pixel width of the drawing surface. */
  readonly cssWidth: number;
  /** CSS pixel height of the drawing surface. */
  readonly cssHeight: number;
  /** Device pixel ratio. */
  readonly dpr: number;
}

export interface Camera {
  /** World metres per CSS pixel. Smaller = more zoomed in. */
  readonly metresPerCssPixel: number;
  /** World x (metres) shown at the viewport centre (pan). */
  readonly worldCentreX: number;
  /** World y (metres) shown at the viewport centre (pan). */
  readonly worldCentreY: number;
}

export interface ScreenPoint {
  /** Device-pixel x. */
  readonly x: number;
  /** Device-pixel y. */
  readonly y: number;
}

export interface WorldPoint {
  readonly x: number;
  readonly y: number;
}

export interface Projection {
  readonly viewport: Viewport;
  readonly camera: Camera;
  /** Map focus-centred SI world metres to device pixels. */
  worldToScreen(wx: number, wy: number): ScreenPoint;
  /** Inverse of worldToScreen: device pixels back to world metres. */
  screenToWorld(px: number, py: number): WorldPoint;
  /** Scale a world length (metres) to device pixels. */
  lengthToScreen(metres: number): number;
}

export function createProjection(viewport: Viewport, camera: Camera): Projection {
  const { cssWidth, cssHeight, dpr } = viewport;
  const { metresPerCssPixel, worldCentreX, worldCentreY } = camera;
  const halfW = (cssWidth * dpr) / 2;
  const halfH = (cssHeight * dpr) / 2;
  // Device pixels per world metre. +y world maps to -y screen (screen y grows down).
  const pxPerMetre = dpr / metresPerCssPixel;

  return {
    viewport,
    camera,
    worldToScreen(wx: number, wy: number): ScreenPoint {
      return {
        x: halfW + (wx - worldCentreX) * pxPerMetre,
        y: halfH - (wy - worldCentreY) * pxPerMetre
      };
    },
    screenToWorld(px: number, py: number): WorldPoint {
      return {
        x: (px - halfW) / pxPerMetre + worldCentreX,
        y: -(py - halfH) / pxPerMetre + worldCentreY
      };
    },
    lengthToScreen(metres: number): number {
      return metres * pxPerMetre;
    }
  };
}

/**
 * Symmetric CSS-pixel inset reserved on every side of the fit so the outermost
 * apoapsis is not hidden behind the persistent chrome (top bar ~64px, Playback
 * HUD ~64px, model notice) plus the edge margin (~32px). Story 3.2 / AC-5: the
 * canvas is full-bleed and the panels FLOAT above it, so a naive fit-to-full-
 * canvas can place the outermost orbit under the chrome. Both adapters pass this
 * to `composeCamera` so the fitted view clears the chrome band. Verified
 * empirically at 1366x768 and 1280x720 (Story 3.2 e2e, AC-5).
 */
export const SAFE_INSET_CSS = 96;

/**
 * Derive a "fit all orbits" camera: choose metres-per-pixel so the largest world
 * radius fits inside the viewport, centred on the focus. This is the single
 * source of the default framing (AD-10), not a per-adapter guess.
 *
 * `safeInsetCss` (Story 3.2, AC-5) reserves a symmetric CSS-pixel band so the
 * outermost apoapsis clears the persistent chrome: the usable half-extent becomes
 * `min(cssWidth, cssHeight)/2 - safeInsetCss`. When `safeInsetCss` is 0/absent the
 * legacy `marginFraction` framing is kept byte-identical so Stories 1.x snapshots
 * do not shift.
 */
export function fitAllCamera(
  viewport: Viewport,
  maxWorldRadiusMetres: number,
  marginFraction = 0.12,
  safeInsetCss = 0
): Camera {
  const half = Math.min(viewport.cssWidth, viewport.cssHeight) / 2;
  const usableHalf = safeInsetCss > 0 ? half - safeInsetCss : half * (1 - marginFraction);
  const safeHalf = usableHalf > 0 ? usableHalf : 1;
  const safeRadius = maxWorldRadiusMetres > 0 ? maxWorldRadiusMetres : 1;
  return {
    metresPerCssPixel: safeRadius / safeHalf,
    worldCentreX: 0,
    worldCentreY: 0
  };
}

/**
 * Compose the interactive camera (Story 3.2, AD-10): fold the zoom/pan modifier
 * over the always-recomputed fit baseline at THIS one site, consumed identically
 * by both renderer adapters and (via `getProjection()`) the ui hit-test/drag
 * inverse. `zoom` is a multiplier over the fit baseline, `panX`/`panY` are the
 * world-metre viewport centre. `zoom = 1, pan = (0,0)` is exactly fit-all, so the
 * view auto-reframes whenever the orbits or the canvas size change (the fit
 * baseline is recomputed every call).
 *
 * Takes plain numbers (not the `state` `ViewCamera` type) so `projection.ts` keeps
 * importing nothing from `state`. A non-finite/non-positive zoom degrades to 1.
 */
export function composeCamera(
  viewport: Viewport,
  maxWorldRadiusMetres: number,
  zoom: number,
  panX: number,
  panY: number,
  safeInsetCss = 0
): Camera {
  const baseline = fitAllCamera(viewport, maxWorldRadiusMetres, 0.12, safeInsetCss);
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  const px = Number.isFinite(panX) ? panX : 0;
  const py = Number.isFinite(panY) ? panY : 0;
  return {
    metresPerCssPixel: baseline.metresPerCssPixel / z,
    worldCentreX: px,
    worldCentreY: py
  };
}
